import { formatCurrency, formatNumber, formatPercent } from "@/components/deal-metrics/shared";

export type ExecutiveMetricKey =
  | "opportunities"
  | "orders"
  | "grossSales"
  | "netSales"
  | "avgOrderValue"
  | "gpmNetOverGross"
  | "gpmCostBased"
  | "closeRate";

export type RecordMetricKey = "opportunities" | "orders" | "grossSales" | "netSales";

export interface ExecMonthlyRow {
  month: string;
  opportunities: number;
  orders: number;
  grossSales: number;
  netSales: number;
  gpmDollars: number;
  avgOrderValue: number;
  gpmNetOverGross: number;
  gpmCostBased: number;
  closeRate: number;
}

export type YoyDeltas = Partial<Record<ExecutiveMetricKey, number | null>>;

export interface AllTimeHigh {
  value: number;
  month: string;
}

export interface CurrentMonthYoy {
  month: string;
  asOfDayOfMonth: number;
  priorRow: ExecMonthlyRow;
}

export interface ExecutiveDashboardResponse {
  rangeStartMonth: string;
  rangeEndMonth: string;
  rows: ExecMonthlyRow[];
  yoyByMonth: Record<string, YoyDeltas>;
  allTimeHighs: Record<RecordMetricKey, AllTimeHigh | null>;
  newRecordsThisMonth: RecordMetricKey[];
  currentMonthYoy: CurrentMonthYoy | null;
  drillContext: {
    prodPipelineId: string | null;
    salesPipelineId: string | null;
    excludeOppStageIds: string[];
    excludeClosedStageIds: string[];
  };
}

type MetricFormat = "count" | "currency" | "percent";

export const METRIC_CONFIG: Record<ExecutiveMetricKey, { label: string; format: MetricFormat }> = {
  opportunities: { label: "Opportunities", format: "count" },
  orders: { label: "Orders", format: "count" },
  grossSales: { label: "Gross Sales", format: "currency" },
  netSales: { label: "Net Sales", format: "currency" },
  avgOrderValue: { label: "Avg Order Value", format: "currency" },
  gpmNetOverGross: { label: "GPM (Net/Gross)", format: "percent" },
  gpmCostBased: { label: "GPM (Cost-based)", format: "percent" },
  closeRate: { label: "Close Rate", format: "percent" },
};

export const METRIC_ORDER: ExecutiveMetricKey[] = [
  "opportunities",
  "orders",
  "grossSales",
  "netSales",
  "avgOrderValue",
  "gpmNetOverGross",
  "gpmCostBased",
  "closeRate",
];

export function formatMetric(value: number, format: MetricFormat): string {
  switch (format) {
    case "count":
      return formatNumber(Math.round(value));
    case "currency":
      return formatCurrency(value);
    case "percent":
      return formatPercent(value);
  }
}

export function formatYoy(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

export function formatMonthLabel(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

export function formatLongMonthLabel(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

export function fyForMonth(ym: string): number {
  const [yStr, mStr] = ym.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  return m >= 4 ? y + 1 : y;
}

const ZERO_ROW: Omit<ExecMonthlyRow, "month"> = {
  opportunities: 0,
  orders: 0,
  grossSales: 0,
  netSales: 0,
  gpmDollars: 0,
  avgOrderValue: 0,
  gpmNetOverGross: 0,
  gpmCostBased: 0,
  closeRate: 0,
};

export function aggregateRows(rows: ExecMonthlyRow[]): ExecMonthlyRow {
  const agg = { month: "", ...ZERO_ROW };
  for (const r of rows) {
    agg.opportunities += r.opportunities;
    agg.orders += r.orders;
    agg.grossSales += r.grossSales;
    agg.netSales += r.netSales;
    agg.gpmDollars += r.gpmDollars;
  }
  agg.avgOrderValue = agg.orders > 0 ? agg.grossSales / agg.orders : 0;
  agg.gpmNetOverGross = agg.grossSales > 0 ? agg.netSales / agg.grossSales : 0;
  agg.gpmCostBased = agg.grossSales > 0 ? agg.gpmDollars / agg.grossSales : 0;
  agg.closeRate = agg.opportunities > 0 ? agg.orders / agg.opportunities : 0;
  return agg;
}

export function computeYoyDeltas(current: ExecMonthlyRow, prior: ExecMonthlyRow): YoyDeltas {
  const deltas: YoyDeltas = {};
  const keys: ExecutiveMetricKey[] = [
    "opportunities",
    "orders",
    "grossSales",
    "netSales",
    "avgOrderValue",
    "gpmNetOverGross",
    "gpmCostBased",
    "closeRate",
  ];
  for (const k of keys) {
    const cur = current[k];
    const prev = prior[k];
    deltas[k] = prev === 0 ? null : ((cur - prev) / prev) * 100;
  }
  return deltas;
}
